import type { Route } from '@angular/router';
import {
  authGuard,
  default as authRoutes,
  roleGuard,
  unAuthGuard,
} from '@szte-griffin/core/auth/router';
import { AppLayoutComponent } from '@szte-griffin/feature/app-layout/ui';
import { adminUserRoutes } from './feature/admin-user/admin-user.routes';
import type { AdminUserRole } from '@cappus/sdk-mecap-backend';
import { inject } from '@angular/core';
import { AUTH_SERVICE_INJECTION_TOKEN } from '@szte-griffin/core/auth/ui';
import { newsRoutes } from './feature/news/news.routes';
import { forbiddenNicknameRoutes } from './feature/forbidden-nickname/forbidden-nickname.routes';
import { eventRoutes } from './feature/event/event.routes';
import { poiRoutes } from './feature/poi/poi.routes';
import { locationRoutes } from './feature/location/location.routes';
import { dirtyCheckCanDeactivateGuard } from '@pct-ng/feature-dirty-check';
import { OnboardingStepRoutes } from './feature/onboarding-step/onboarding-step.routes';
import { offerRoutes } from './feature/offer/offer.routes';
import { resourceRoutes } from './feature/resource/resource.routes';
import { notificationRoutes } from './feature/notification/notification.routes';

function i18nPrefix(name: string): string {
  return `PAGES.${name}.BREADCRUMB.LIST`;
}
export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: (): string =>
      inject(AUTH_SERVICE_INJECTION_TOKEN).hasRealmRole('SystemAdmin') ? '/admin-user' : '/news',
  },
  { path: '', canActivateChild: [unAuthGuard], children: authRoutes },
  {
    path: '',
    component: AppLayoutComponent,
    canActivateChild: [authGuard],
    children: [
      {
        path: 'admin-user',
        children: adminUserRoutes,
        title: i18nPrefix('ADMIN_USER'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'news',
        children: newsRoutes,
        title: i18nPrefix('NEWS'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'event',
        children: eventRoutes,
        title: i18nPrefix('EVENT'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'offer',
        children: offerRoutes,
        title: i18nPrefix('OFFER'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'location',
        children: locationRoutes,
        title: i18nPrefix('LOCATION'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'poi',
        children: poiRoutes,
        title: i18nPrefix('POI'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'resource',
        children: resourceRoutes,
        title: i18nPrefix('RESOURCE'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'forbidden-nickname',
        children: forbiddenNicknameRoutes,
        title: i18nPrefix('FORBIDDEN_NICKNAME'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'onboarding-step',
        children: OnboardingStepRoutes,
        title: i18nPrefix('ONBOARDING_STEP'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'notification',
        children: notificationRoutes,
        title: i18nPrefix('NOTIFICATIONS'),
        canActivate: [roleGuard],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./feature/my-profile/my-profile.component').then(
            ({ MyProfileComponent }) => MyProfileComponent,
          ),
        title: i18nPrefix('MY_PROFILE'),
        canActivate: [roleGuard],
        canDeactivate: [dirtyCheckCanDeactivateGuard()],
        data: {
          roles: ['SystemAdmin', 'Admin'] satisfies AdminUserRole[],
        },
      },
    ],
  },
];
